import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Code2,
  Zap,
  FileText,
  Sparkles,
  Check,
} from 'lucide-react';

export const Route = createFileRoute('/products/')({
  component: ProductsPage,
});

const productCards = [
  {
    id: 'codespringforge',
    name: 'CodeSpringForge',
    tagline: 'From Code to Clarity',
    description:
      'AI-powered code analysis and PRD generation. Transform how your team understands and documents software.',
    icon: Code2,
    status: 'available',
    href: '/products/codespringforge',
    features: [
      'Repository Analysis',
      'Feature Extraction',
      'PRD Generation',
      'Security Scanning',
    ],
  },
  {
    id: 'aicodeforge',
    name: 'AICodeForge',
    tagline: 'Code Smarter, Ship Faster',
    description:
      'Intelligent code generation and refactoring powered by AI. Turn natural language into production-ready code.',
    icon: Zap,
    status: 'available',
    href: '/products/aicodeforge',
    features: [
      'Code Generation',
      'Smart Refactoring',
      'Test Generation',
      'Code Review AI',
    ],
  },
  {
    id: 'sdd-forge',
    name: 'SDD-Forge',
    tagline: 'Design Documents That Write Themselves',
    description:
      'Automated software design document generation. From architecture diagrams to API specifications.',
    icon: FileText,
    status: 'available',
    href: '/products/sdd-forge',
    features: [
      'Architecture Docs',
      'API Specifications',
      'Sequence Diagrams',
      'Data Flow Docs',
    ],
  },
];

function ProductsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              {/* Fixed: contrast 5.74:1 (was 4.48:1 with text-primary) */}
              <div className="inline-flex items-center gap-2 rounded-full border bg-blue-100 px-4 py-1.5 text-blue-700 mb-6">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">Product Suite</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                Tools for Every Stage of{' '}
                <span className="text-primary">Development</span>
              </h1>
              <p className="mt-6 text-xl text-muted-foreground">
                ForgeNetwork offers a unified ecosystem of AI-powered tools designed to
                help engineering teams ship better software, faster.
              </p>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-3">
              {productCards.map((product) => (
                <div
                  key={product.id}
                  className="relative rounded-2xl border bg-card p-8 hover:border-primary/50 transition-all hover:shadow-lg"
                >
                  {product.status === 'coming-soon' && (
                    <div className="absolute top-4 right-4 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                      Coming Soon
                    </div>
                  )}

                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 mb-6">
                    <product.icon className="h-7 w-7 text-primary" />
                  </div>

                  <h2 className="text-2xl font-bold">{product.name}</h2>
                  <p className="mt-1 text-primary font-medium">{product.tagline}</p>
                  <p className="mt-4 text-muted-foreground">{product.description}</p>

                  <ul className="mt-6 space-y-3">
                    {product.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-primary" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    {product.status === 'available' ? (
                      <Link to={product.href}>
                        <Button className="w-full gap-2">
                          Learn More <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    ) : (
                      <Button className="w-full" variant="outline" disabled>
                        Coming Soon
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Section */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Better Together
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                ForgeNetwork products are designed to work seamlessly together,
                creating a unified developer experience.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  title: 'Shared Context',
                  description:
                    'Analysis from CodeSpringForge feeds directly into AICodeForge and SDD-Forge for consistent, context-aware outputs.',
                },
                {
                  title: 'Unified Workspace',
                  description:
                    'One workspace, one team, one billing. Manage all your ForgeNetwork tools in one place.',
                },
                {
                  title: 'Single API',
                  description:
                    'Integrate once, access everything. Our unified API gives you programmatic access to all products.',
                },
              ].map((feature) => (
                <div key={feature.title} className="text-center">
                  <h3 className="text-lg font-semibold">{feature.title}</h3>
                  <p className="mt-2 text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Start with CodeSpringForge
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Begin your ForgeNetwork journey with our flagship product. Analyze your
                first repository in minutes.
              </p>
              <div className="mt-8">
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    Get Started Free <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
